import { supabase } from '@/integrations/supabase/client';
import { 
  PEOOrganizationalRole, 
  PEOTeamStructure, 
  EnhancedPEORepresentative,
  PEOTeamMember,
  PEOOrgChart,
  OrgChartNode,
  UserAccessScope,
  PermissionCheckResult,
  TeamManagementOperation,
  PEORoleType,
  PermissionLevel
} from '@/types/peo-organization.types';

export class PEOOrganizationService {
  // ============ ORGANIZATIONAL ROLES ============
  
  static async getOrganizationalRoles(providerId: string): Promise<PEOOrganizationalRole[]> {
    const { data, error } = await supabase
      .from('peo_organizational_roles')
      .select('*')
      .eq('provider_id', providerId)
      .order('role_name');
    
    if (error) throw error;
    return data || [];
  }

  static async createOrganizationalRole(role: Omit<PEOOrganizationalRole, 'id' | 'created_at' | 'updated_at'>): Promise<PEOOrganizationalRole> {
    const { data, error } = await supabase
      .from('peo_organizational_roles')
      .insert([role])
      .select()
      .single();
    
    if (error) throw error;

    // Log activity
    await supabase.rpc('log_organization_activity', {
      p_provider_id: role.provider_id,
      p_action_type: 'role_created',
      p_action_description: `New organizational role created: ${role.role_name}`,
      p_entity_type: 'role',
      p_entity_id: data.id,
      p_metadata: { role_type: role.role_type, permission_level: role.permission_level }
    });

    return data;
  }

  static async updateOrganizationalRole(id: string, updates: Partial<PEOOrganizationalRole>): Promise<PEOOrganizationalRole> {
    const { data, error } = await supabase
      .from('peo_organizational_roles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  static async deleteOrganizationalRole(id: string): Promise<void> {
    const { error } = await supabase
      .from('peo_organizational_roles')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }

  // ============ TEAM STRUCTURE ============

  static async getTeamStructure(providerId: string): Promise<PEOTeamStructure[]> {
    const { data, error } = await supabase
      .from('peo_team_structure')
      .select('*')
      .eq('provider_id', providerId)
      .order('hierarchy_level');
    
    if (error) throw error;
    return data || [];
  }

  static async createTeamStructure(structure: Omit<PEOTeamStructure, 'id' | 'created_at' | 'updated_at'>): Promise<PEOTeamStructure> {
    const { data, error } = await supabase
      .from('peo_team_structure')
      .insert([structure])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  static async updateTeamStructure(id: string, updates: Partial<PEOTeamStructure>): Promise<PEOTeamStructure> {
    const { data, error } = await supabase
      .from('peo_team_structure')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  static async deleteTeamStructure(id: string): Promise<void> {
    const { error } = await supabase
      .from('peo_team_structure')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }

  // ============ PEO REPRESENTATIVES (Enhanced) ============

  static async getEnhancedRepresentatives(providerId: string): Promise<EnhancedPEORepresentative[]> {
    // Get basic representative data with organizational roles
    const { data: repsData, error: repsError } = await supabase
      .from('peo_representatives')
      .select(`
        *,
        organizational_role:peo_organizational_roles(*)
      `)
      .eq('provider_id', providerId)
      .order('created_at');
    
    if (repsError) throw repsError;
    if (!repsData) return [];

    // Get profile data for all users
    const userIds = repsData.map(rep => rep.user_id);
    const managerIds = repsData.map(rep => rep.manager_user_id).filter(Boolean);
    const allUserIds = [...new Set([...userIds, ...managerIds])];

    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email')
      .in('id', allUserIds);

    if (profilesError) throw profilesError;

    // Create a map of profiles for easy lookup
    const profilesMap = new Map(
      (profilesData || []).map(profile => [profile.id, profile])
    );

    // Combine the data
    const enhancedReps: EnhancedPEORepresentative[] = repsData.map(rep => ({
      ...rep,
      verification_status: rep.verification_status as 'pending' | 'verified' | 'rejected',
      profile: profilesMap.get(rep.user_id) || undefined,
      manager_profile: rep.manager_user_id ? profilesMap.get(rep.manager_user_id) || undefined : undefined
    }));

    return enhancedReps;
  }

  static async updateRepresentativeRole(userId: string, organizationalRoleId: string): Promise<void> {
    // Get provider ID and role name for activity logging
    const { data: rep } = await supabase
      .from('peo_representatives')
      .select('provider_id')
      .eq('user_id', userId)
      .single();

    const { data: role } = await supabase
      .from('peo_organizational_roles')
      .select('role_name')
      .eq('id', organizationalRoleId)
      .single();

    const { data: profile } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', userId)
      .single();

    const { error } = await supabase
      .from('peo_representatives')
      .update({ organizational_role_id: organizationalRoleId })
      .eq('user_id', userId);
    
    if (error) throw error;

    // Log activity
    if (rep && role && profile) {
      const userName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      try {
        await supabase.rpc('log_organization_activity', {
          p_provider_id: rep.provider_id,
          p_action_type: 'role_assigned',
          p_action_description: `${userName} assigned to ${role.role_name} role`,
          p_entity_type: 'role',
          p_entity_id: organizationalRoleId,
          p_metadata: { user_id: userId, role_id: organizationalRoleId }
        });
      } catch (activityError) {
        // Activity logging failed, but don't block the main operation
        console.warn('Failed to log activity:', activityError);
      }
    }
  }

  static async updateRepresentativeManager(userId: string, managerUserId: string | null): Promise<void> {
    // Get provider ID and names for activity logging
    const { data: rep } = await supabase
      .from('peo_representatives')
      .select('provider_id')
      .eq('user_id', userId)
      .single();

    const { data: userProfile } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', userId)
      .single();

    let managerProfile = null;
    if (managerUserId) {
      const { data: manager } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', managerUserId)
        .single();
      managerProfile = manager;
    }

    const { error } = await supabase
      .from('peo_representatives')
      .update({ manager_user_id: managerUserId })
      .eq('user_id', userId);
    
    if (error) throw error;

    // Log activity
    if (rep && userProfile) {
      const userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
      const managerName = managerProfile 
        ? `${managerProfile.first_name || ''} ${managerProfile.last_name || ''}`.trim()
        : 'no manager';
      
      await supabase.rpc('log_organization_activity', {
        p_provider_id: rep.provider_id,
        p_action_type: 'team_updated',
        p_action_description: `${userName} assigned to report to ${managerName}`,
        p_entity_type: 'team_member',
        p_entity_id: userId,
        p_metadata: { user_id: userId, manager_id: managerUserId }
      });
    }
  }

  static async updateRepresentativeTeamInfo(
    userId: string, 
    teamInfo: {
      team_name?: string;
      department?: string;
      region?: string;
      territory?: string[];
    }
  ): Promise<void> {
    const { error } = await supabase
      .from('peo_representatives')
      .update(teamInfo)
      .eq('user_id', userId);
    
    if (error) throw error;
  }

  // ============ ORGANIZATION CHART ============

  static async getOrganizationChart(providerId: string): Promise<PEOOrgChart> {
    // Get all enhanced representatives
    const representatives = await this.getEnhancedRepresentatives(providerId);
    
    // Get team structure
    const teamStructure = await this.getTeamStructure(providerId);
    
    // Get provider info
    const { data: provider, error: providerError } = await supabase
      .from('peo_providers')
      .select('id, name')
      .eq('id', providerId)
      .single();
    
    if (providerError) throw providerError;

    // Build hierarchical structure
    const memberMap = new Map<string, PEOTeamMember>();
    const rootMembers: PEOTeamMember[] = [];

    // Initialize all members
    representatives.forEach(rep => {
      const member: PEOTeamMember = {
        ...rep,
        direct_reports: [],
        hierarchy_level: 1
      };
      memberMap.set(rep.user_id, member);
    });

    // Build hierarchy based on team structure
    teamStructure.forEach(structure => {
      const child = memberMap.get(structure.child_user_id);
      const parent = structure.parent_user_id ? memberMap.get(structure.parent_user_id) : null;
      
      if (child) {
        child.hierarchy_level = structure.hierarchy_level;
        
        if (parent) {
          parent.direct_reports!.push(child);
          parent.is_manager = true;
        } else {
          rootMembers.push(child);
        }
      }
    });

    // Add members who are not in team structure as root members
    representatives.forEach(rep => {
      const member = memberMap.get(rep.user_id);
      if (member && !teamStructure.some(s => s.child_user_id === rep.user_id)) {
        rootMembers.push(member);
      }
    });

    // Get unique departments, teams, and regions
    const departments = [...new Set(representatives.map(r => r.department).filter(Boolean))];
    const teams = [...new Set(representatives.map(r => r.team_name).filter(Boolean))];
    const regions = [...new Set(representatives.map(r => r.region).filter(Boolean))];

    return {
      provider_id: providerId,
      provider_name: provider.name,
      members: Array.from(memberMap.values()),
      departments: departments as string[],
      teams: teams as string[],
      regions: regions as string[]
    };
  }

  static async getOrgChartNodes(providerId: string): Promise<OrgChartNode[]> {
    const orgChart = await this.getOrganizationChart(providerId);
    
    const convertToNodes = (members: PEOTeamMember[], parentId?: string): OrgChartNode[] => {
      return members.map(member => ({
        id: member.user_id,
        user_id: member.user_id,
        name: member.profile ? `${member.profile.first_name || ''} ${member.profile.last_name || ''}`.trim() : 'Unknown',
        title: member.organizational_role?.role_name || 'No Role Assigned',
        role_type: member.organizational_role?.role_type || null,
        department: member.department,
        team: member.team_name,
        region: member.region,
        parent_id: parentId,
        children: convertToNodes(member.direct_reports || [], member.user_id),
        level: member.hierarchy_level || 1,
        is_manager: member.is_manager,
        direct_reports_count: member.direct_reports?.length || 0
      }));
    };

    // Get root level members (those without parents)
    const rootMembers = orgChart.members.filter(member => 
      !orgChart.members.some(other => 
        other.direct_reports?.some(report => report.user_id === member.user_id)
      )
    );

    return convertToNodes(rootMembers);
  }

  // ============ PERMISSION CHECKING ============

  static async checkUserPermissions(
    requestingUserId: string, 
    targetUserId: string, 
    dataType: string = 'application'
  ): Promise<PermissionCheckResult> {
    const { data, error } = await supabase.rpc('can_peo_user_access_data', {
      requesting_user_id: requestingUserId,
      target_user_id: targetUserId,
      data_type: dataType
    });

    if (error) throw error;

    return {
      can_access: data,
      scope: 'own' // This would need to be enhanced to return the actual scope
    };
  }

  static async getUserAccessScope(userId: string): Promise<UserAccessScope | null> {
    // Get user's representative info with role
    const { data, error } = await supabase
      .from('peo_representatives')
      .select(`
        *,
        organizational_role:peo_organizational_roles(*)
      `)
      .eq('user_id', userId)
      .eq('verification_status', 'verified')
      .single();

    if (error) return null;
    if (!data) return null;

    // Get subordinates
    const { data: subordinates, error: subError } = await supabase.rpc('get_peo_user_subordinates', {
      user_id: userId
    });

    if (subError) throw subError;

    const accessibleUserIds = [userId, ...(subordinates?.map((s: any) => s.subordinate_user_id) || [])];

    return {
      user_id: userId,
      provider_id: data.provider_id,
      permission_level: data.organizational_role?.permission_level || 'own_only',
      accessible_user_ids: accessibleUserIds,
      departments: data.department ? [data.department] : [],
      regions: data.region ? [data.region] : [],
      teams: data.team_name ? [data.team_name] : []
    };
  }

  // ============ TEAM MANAGEMENT OPERATIONS ============

  static async performTeamOperation(operation: TeamManagementOperation): Promise<void> {
    switch (operation.type) {
      case 'assign_role':
        await this.updateRepresentativeRole(operation.user_id, operation.new_value);
        break;
      
      case 'change_manager':
        await this.updateRepresentativeManager(operation.user_id, operation.target_id || null);
        
        // Also update team structure if needed
        if (operation.target_id) {
          const { data: existingStructure } = await supabase
            .from('peo_team_structure')
            .select('*')
            .eq('child_user_id', operation.user_id)
            .single();

          if (existingStructure) {
            await this.updateTeamStructure(existingStructure.id, {
              parent_user_id: operation.target_id
            });
          } else {
            // Create new team structure
            const { data: childRep } = await supabase
              .from('peo_representatives')
              .select('provider_id')
              .eq('user_id', operation.user_id)
              .single();

            if (childRep) {
              await this.createTeamStructure({
                provider_id: childRep.provider_id,
                parent_user_id: operation.target_id,
                child_user_id: operation.user_id,
                hierarchy_level: 2, // Default level
                created_by: operation.user_id // This should be the admin performing the operation
              });
            }
          }
        }
        break;
      
      case 'move_team':
        await this.updateRepresentativeTeamInfo(operation.user_id, operation.new_value);
        break;
      
      case 'update_permissions':
        if (operation.target_id) {
          await this.updateOrganizationalRole(operation.target_id, operation.new_value);
        }
        break;
    }
  }

  // ============ BULK OPERATIONS ============

  static async bulkAssignRole(userIds: string[], roleId: string): Promise<void> {
    const { error } = await supabase
      .from('peo_representatives')
      .update({ organizational_role_id: roleId })
      .in('user_id', userIds);

    if (error) throw error;
  }

  static async bulkMoveTeam(userIds: string[], teamInfo: {
    team_name?: string;
    department?: string;
    region?: string;
  }): Promise<void> {
    const { error } = await supabase
      .from('peo_representatives')
      .update(teamInfo)
      .in('user_id', userIds);

    if (error) throw error;
  }

  // ============ ANALYTICS ============

  static async getOrganizationAnalytics(providerId: string) {
    const representatives = await this.getEnhancedRepresentatives(providerId);
    const roles = await this.getOrganizationalRoles(providerId);
    
    const roleDistribution = roles.map(role => ({
      role_name: role.role_name,
      count: representatives.filter(rep => rep.organizational_role_id === role.id).length
    }));

    const departmentDistribution = [...new Set(representatives.map(r => r.department).filter(Boolean))]
      .map(dept => ({
        department: dept,
        count: representatives.filter(rep => rep.department === dept).length
      }));

    const teamDistribution = [...new Set(representatives.map(r => r.team_name).filter(Boolean))]
      .map(team => ({
        team: team,
        count: representatives.filter(rep => rep.team_name === team).length
      }));

    return {
      total_members: representatives.length,
      verified_members: representatives.filter(rep => rep.verification_status === 'verified').length,
      pending_members: representatives.filter(rep => rep.verification_status === 'pending').length,
      managers_count: representatives.filter(rep => rep.is_manager).length,
      role_distribution: roleDistribution,
      department_distribution: departmentDistribution,
      team_distribution: teamDistribution,
      total_roles: roles.length,
      total_departments: departmentDistribution.length,
      total_teams: teamDistribution.length
    };
  }
}